<script setup lang="ts">
const { t: $t } = useI18n()

import type { SelectOption } from 'naive-ui'
import type { ApiCompanyVehicleGetAllAvailableForRental } from '~/server/trpc/routers/companyVehicle'
import type { CompanyVehicleRentalCreateOrUpdate } from '~/server/trpc/routers/companyVehicleRental'
import { CompanyVehicleRentalCondition, CompanyVehicleRentalTankFilling } from '~/prisma/enums'

const props = defineProps<{
  payload: CompanyVehicleRentalCreateOrUpdate
  possibleDrivers: SelectOption[]
  availableCompanyVehicles: ApiCompanyVehicleGetAllAvailableForRental[]
  isPending?: boolean
  mode?: 'output' | null
}>()
const emit = defineEmits<{ (e: 'save', payload: CompanyVehicleRentalCreateOrUpdate): void }>()

const skipSignature = shallowRef(false)

const { cloned } = useCloned<CompanyVehicleRentalCreateOrUpdate>(props.payload)

const companyVehicleOptions = computed(() => {
  return props.availableCompanyVehicles?.map(vehicle => ({
    label: `${vehicle.producerCompanyName} ${vehicle.vehicleModelName} (${vehicle.licensePlate})`,
    value: vehicle.id,
  })) ?? []
})

const conditionOptions = computed(() => {
  return Object.entries(CompanyVehicleRentalCondition).map(([key, value]) => ({
    label: $t(`companyVehicleRental.field.condition.${key}`),
    value,
  }))
})

const tankFillingOptions = computed(() => {
  return Object.entries(CompanyVehicleRentalTankFilling).map(([key, value]) => ({
    label: $t(`companyVehicleRental.field.tankFilling.${key}`),
    value,
  }))
})

watch(() => cloned.value.data.companyVehicleId, (companyVehicleId) => {
  if (companyVehicleId) {
    const selectedVehicle = props.availableCompanyVehicles.find(vehicle => vehicle.id === companyVehicleId)
    cloned.value.data.mileageStartOfRental = selectedVehicle?.mileage ?? 0
  }
})

watch(() => cloned.value.data.isExternalRental, (isExternalRental) => {
  if (isExternalRental) {
    cloned.value.data.internalDriverEmail = null
    return
  }

  cloned.value.data.externalDriverName = null
  cloned.value.data.externalDriverPhone = null
})

const disableInternalDriverSelection = computed(() => cloned.value.data.isExternalRental || cloned.value.mode === 'update')
const mode = computed(() => props.mode ?? cloned.value.mode)
const showInternalDriverSelection = computed(() => ['create', 'update'].includes(mode.value) && !cloned.value.data.isExternalRental)
const showExternalDriverFields = computed(() => ['create', 'update'].includes(mode.value) && cloned.value.data.isExternalRental)
</script>

<template>
  <FormKit
    v-model="cloned.data"
    type="form"
    :actions="false"
    @submit="emit('save', cloned as CompanyVehicleRentalCreateOrUpdate)"
  >
    <TheDevOnlyNiceJson :data="cloned.data" />
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-2">
      <FormKitMultiSelect
        v-if="['create', 'update'].includes(mode)"
        outer-class="max-w-md"
        name="companyVehicleId"
        :multi-select-options="companyVehicleOptions"
        :is-multi-select-allowed="false"
        validation="required"
        :label="$t('companyVehicle.name')"
        :disabled="cloned.mode === 'update'"
      />
      <FormKitMultiSelect
        v-if="showInternalDriverSelection"
        outer-class="max-w-md"
        name="internalDriverEmail"
        :multi-select-options="possibleDrivers"
        :is-multi-select-allowed="false"
        :validation="disableInternalDriverSelection ? '' : 'required'"
        :label="$t('companyVehicleRental.field.internalDriver.name')"
        :disabled="disableInternalDriverSelection"
      />

      <FormKit
        v-if="showExternalDriverFields"
        name="externalDriverName"
        type="text"
        validation="required"
        :label="$t('companyVehicleRental.field.externalDriverName.name')"
        :disabled="mode === 'update'"
      />

      <FormKit
        v-if="showExternalDriverFields"
        name="externalDriverPhone"
        type="text"
        validation="required"
        :label="$t('companyVehicleRental.field.externalDriverPhone.name')"
        :disabled="mode === 'update'"
      />

      <FormKitDateTime
        v-if="['create', 'update'].includes(mode)"
        name="rentalStartsAt"
        :label="$t('companyVehicleRental.field.rentalStartsAt.name')"
        validation="required"
      />
      <FormKitDateTime
        v-if="['create', 'update'].includes(mode)"
        name="expectedRentalEndsAt"
        :label="$t('companyVehicleRental.field.expectedRentalEndsAt.name')"
        validation="required"
      />

      <FormKit
        v-if="mode === 'output'"
        name="tankFillingStartOfRental"
        type="select"
        :options="tankFillingOptions"
        validation="required"
        :label="$t('companyVehicleRental.field.tankFilling.name')"
      />
      <FormKit
        v-if="mode === 'output'"
        name="conditionStartOfRental"
        type="select"
        :options="conditionOptions"
        validation="required"
        :label="$t('companyVehicleRental.field.condition.name')"
      />

      <FormKit
        v-if="mode === 'output'"
        name="mileageStartOfRental"
        type="number"
        number
        validation="required"
        :label="$t('companyVehicle.field.mileage.name')"
      />
      <FormKit
        v-if="['create', 'update'].includes(mode)"
        outer-class="flex items-center"
        name="isExternalRental"
        type="checkbox"
        :label="$t('companyVehicleRental.field.isExternalRental.name')"
        :disabled="mode === 'update'"
      />
    </div>

    <FormKitAddressSelector
      v-if="['create', 'update'].includes(mode)"
      name="destination"
      :disable-max-width="true"
      :label="$t('companyVehicleRental.field.destination.name')"
    />

    <FormKit
      v-if="mode === 'output'"
      name="additionalInformation"
      type="textarea"
      :label="$t('companyVehicleRental.field.additionalInformation.name')"
    />

    <div
      v-if="['create', 'output'].includes(mode) || (cloned.data.driverRentalStartSignature && mode === 'update')"
      class="flex flex-col mb-4"
    >
      <FormKitSignaturePopup
        name="driverRentalStartSignature"
        :label="$t('companyVehicleRental.field.signatureDriver.name')"
        :button-text="$t('companyVehicleRental.field.signatureDriver.prompt')"
        :title="$t('companyVehicleRental.signaturePopup.title')"
        :disable-by-pass="true"
        :disable-positions-overview="true"
        :default="undefined"
        :validation="(!skipSignature && mode !== 'output') || mode === 'output' ? 'required' : ''"
        :read-only="mode === 'update' || (cloned.data.driverRentalStartSignature && mode === 'output')"
      />

      <n-checkbox
        v-if="mode === 'create'"
        v-model:checked="skipSignature"
        :label="$t('companyVehicleRental.field.driverNotAvailable.name')"
      />
    </div>

    <FormKitFileDropzone
      v-if="skipSignature || props.payload.data.keyLocationPhotosStartOfRental.length > 0"
      name="keyLocationPhotosStartOfRental"
      label="Hinweis Schlüsselablage"
      accept="image/png,image/jpg,image/jpeg"
    />

    <FormKitFileDropzone
      v-if="mode === 'output'"
      name="vehiclePhotosStartOfRental"
      :label="$t('companyVehicleRental.field.vehiclePhotos.name')"
      accept="image/png,image/jpg,image/jpeg"
      validation="required"
    />
    <FormKit type="submit" :disabled="isPending">
      {{ $t('button.save') }}
    </FormKit>
  </FormKit>
</template>
